/**
 * Launchpad — unified DVA model.
 *
 * Serves every entity type surfaced by the /launchpad page except processes
 * (which keep their own slice in `processes/launchpadHome` to preserve the
 * existing subgroup lazy-loading flow).
 *
 * State layout:
 *
 *   state.launchpad.home[typeId] = {
 *     items, groups, favorites,
 *     progress: { itemsLoaded, itemsTotal },
 *     loading, loaded, error,
 *   }
 */

import { getAll } from '@/services/api';
import request from '@/utils/infrastructure/communication/request';

import { ENTITY_TYPES } from './entityTypes';

const FIRST_PAGE_SIZE = 60;
const GROUPS_LIMIT = 500;

const emptyHome = () => ({
  items: [],
  groups: [],
  favorites: [],
  progress: { itemsLoaded: 0, itemsTotal: 0 },
  loading: false,
  loaded: false,
  error: null,
  /**
   * Epoch monotónico por entidad. Cada start/reset lo incrementa; las
   * sagas validan su epoch antes de escribir resultados para descartar
   * respuestas que llegaron tarde después de un cambio de tenant.
   */
  epoch: 0,
});

// Types handled by this model (processes are handled elsewhere).
const TYPES = Object.values(ENTITY_TYPES).filter((t) => t.source === 'launchpad');

const initialHome = TYPES.reduce((acc, t) => {
  acc[t.id] = emptyHome();
  return acc;
}, {});

// Extract items from the multiple response shapes the backend uses.
const extractItems = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data)) return res.data;
  return [];
};

const extractTotal = (res, fallback) => {
  if (res == null) return fallback;
  if (typeof res.totalCount === 'number') return res.totalCount;
  if (typeof res.count === 'number') return res.count;
  if (Array.isArray(res)) return res.length;
  return fallback;
};

const Model = {
  namespace: 'launchpad',

  state: {
    home: initialHome,
  },

  effects: {
    // ────────────────────────────────────────────────────────────────
    // Progressive load for a single entity type.
    // Parallel fetch of (allowed, groups?, favorites?) → first page →
    // kick off the remaining pages in the background.
    //
    // `takeLatest` + epoch guard: tenant switch dispatches `resetAll`
    // (que bumpea epoch) y el hook re-emite `fetchHome`. takeLatest
    // cancela la saga vieja, y el epoch guard evita que cualquier
    // respuesta que se "coló" entre el call y el put antes de la
    // cancelación termine escribiendo en la slice nueva.
    // ────────────────────────────────────────────────────────────────
    fetchHome: [
      function* ({ payload }, { call, put, all, select, cancelled }) {
        const type = ENTITY_TYPES[payload?.type];
        if (!type || type.source !== 'launchpad') return;

        yield put({ type: 'homeStart', payload: { type: type.id } });
        const myEpoch = yield select((s) => s.launchpad?.home?.[type.id]?.epoch || 0);
        const isStale = function* () {
          const current = yield select((s) => s.launchpad?.home?.[type.id]?.epoch || 0);
          return current !== myEpoch;
        };

        try {
          // Admins (USER_ADMIN_ROLES) need to see inactive groups/items so
          // they can manage them. Regular users only get the active set.
          const includeInactive = !!payload?.includeInactive;
          // After create/update, callers pass `readPriority: true` so the
          // GET hits the primary replica (`AUTOMY-Read-Priority: primary`).
          const readPriorityParam = payload?.readPriority ? { readPriority: true } : {};
          // Per-type override (jobs: active recurring OR manual-only).
          const activeFilter = includeInactive
            ? undefined
            : type.activeODataFilter || 'isActive eq true';
          const filterParam = activeFilter ? { filter: activeFilter } : {};
          // Admin-only items endpoint. The user-facing `/allowed` endpoints
          // hide inactive records (and filter by visibility) even when no
          // `$filter` is supplied, so admins must hit the management
          // endpoint when one is declared on the entity type.
          const useAdminItems = includeInactive && type.routes.adminAllowed;
          const itemsRoute = useAdminItems ? type.routes.adminAllowed : type.routes.allowed;
          const itemsQuery = useAdminItems
            ? type.queries.adminAllowed || type.queries.allowed || {}
            : type.queries.allowed || {};
          const calls = {
            items: call(getAll, {
              route: itemsRoute,
              page: 1,
              limit: FIRST_PAGE_SIZE,
              ...filterParam,
              ...itemsQuery,
              ...readPriorityParam,
            }),
          };
          // Admin-only ADDITIONAL items (e.g. report/adminReports for
          // reports). Unlike `adminAllowed` these don't replace the regular
          // `allowed` set; they're merged in and tagged with
          // `isSystemReport: true` so the UI can offer a "System" chip.
          const useSystemItems = includeInactive && type.routes.systemAllowed;
          if (useSystemItems) {
            const systemQuery = type.queries.systemAllowed || type.queries.allowed || {};
            calls.systemItems = call(getAll, {
              route: type.routes.systemAllowed,
              page: 1,
              limit: FIRST_PAGE_SIZE,
              ...systemQuery,
              ...readPriorityParam,
            });
          }
          if (type.hasGroups && type.routes.groups) {
            // Admins switch to the management groups endpoint when one is
            // declared. Default endpoints filter groups by visibility (e.g.
            // groups whose processes the user can launch), so admins miss
            // empty groups and need the unfiltered tenant view.
            const useAdminGroups = includeInactive && type.routes.adminGroups;
            const groupsRoute = useAdminGroups ? type.routes.adminGroups : type.routes.groups;
            const groupsQuery = useAdminGroups
              ? type.queries.adminGroups || type.queries.groups || {}
              : type.queries.groups || {};
            // Some entities (today: `connections`) expose group endpoints
            // whose underlying entity doesn't carry an `isActive` flag, so
            // appending `$filter=isActive eq true` makes the backend return
            // 400. Opt-in flag declared on the entity type registry.
            const groupsFilterParam = type.groupsHaveActiveFlag === false ? {} : filterParam;
            calls.groups = call(getAll, {
              route: groupsRoute,
              page: 1,
              limit: GROUPS_LIMIT,
              ...groupsFilterParam,
              ...groupsQuery,
              ...readPriorityParam,
            });
          }
          if (type.queries.favorites) {
            calls.favorites = call(getAll, {
              route: type.routes.favorites,
              page: 1,
              limit: GROUPS_LIMIT,
              ...filterParam,
              ...(type.queries.favorites || {}),
              ...readPriorityParam,
            });
          }

          const res = yield all(calls);
          // El epoch pudo cambiar mientras esperábamos las llamadas
          // paralelas (cambio de tenant). Si cambió, descartamos.
          if (yield* isStale()) return;

          const baseItems = extractItems(res.items);
          // Merge any admin-only "extra" items (currently: system reports)
          // by tagging them with `isSystemReport: true`. They sit in the
          // same `items` collection so groups, search and chips work as-is.
          const systemItems = res.systemItems
            ? extractItems(res.systemItems).map((it) => ({ ...it, isSystemReport: true }))
            : [];
          const items = systemItems.length ? [...baseItems, ...systemItems] : baseItems;
          const groups = res.groups ? extractItems(res.groups) : [];
          const favorites = res.favorites ? extractItems(res.favorites) : [];
          const baseTotal = extractTotal(res.items, baseItems.length);
          const total = baseTotal + systemItems.length;

          yield put({
            type: 'homeSave',
            payload: {
              type: type.id,
              items,
              groups,
              favorites,
              progress: { itemsLoaded: items.length, itemsTotal: total },
            },
          });

          // Only the regular `allowed`/`adminAllowed` set is paginated in
          // the background. System items already come in full on page one.
          if (baseItems.length < baseTotal) {
            yield put({
              type: 'fetchRest',
              payload: {
                type: type.id,
                page: 2,
                limit: FIRST_PAGE_SIZE,
                total,
                includeInactive,
                readPriority: !!payload?.readPriority,
                // Hereda el epoch del fetch principal para que la
                // paginación de fondo también valide su vigencia.
                epoch: myEpoch,
              },
            });
          }
        } catch (e) {
          // Distinguimos cancelación (takeLatest) de error real, y además
          // descartamos respuestas que llegaron tarde tras un reset.
          if (yield cancelled()) return;
          if (yield* isStale()) return;
          yield put({
            type: 'homeError',
            payload: { type: type.id, error: e?.message || 'error' },
          });
        }
      },
      { type: 'takeLatest' },
    ],

    // Paginated background fetch for the remaining records.
    // `takeLatest` + epoch guard: lo mismo que `fetchHome`. La
    // paginación es la culpable más probable de pintar errores fantasma
    // tras un cambio de tenant porque vive más tiempo que el primer
    // fetch (varias páginas) — sin el guard podía seguir corriendo y
    // golpear la slice nueva con su `homeError`.
    fetchRest: [
      function* ({ payload }, { call, put, select, cancelled }) {
        const type = ENTITY_TYPES[payload?.type];
        if (!type || type.source !== 'launchpad') return;
        const {
          page: startPage = 2,
          limit = FIRST_PAGE_SIZE,
          total = 0,
          includeInactive = false,
          readPriority = false,
          epoch: myEpoch = null,
        } = payload;

        const isStale = function* () {
          if (myEpoch == null) return false;
          const current = yield select((s) => s.launchpad?.home?.[type.id]?.epoch || 0);
          return current !== myEpoch;
        };

        let page = startPage;
        let safety = 0;
        // Mirror `fetchHome`: keep paginating against the admin endpoint
        // when applicable so inactive items remain visible across pages.
        const useAdminItems = includeInactive && type.routes.adminAllowed;
        const itemsRoute = useAdminItems ? type.routes.adminAllowed : type.routes.allowed;
        const itemsQuery = useAdminItems
          ? type.queries.adminAllowed || type.queries.allowed || {}
          : type.queries.allowed || {};
        const readPriorityParam = readPriority ? { readPriority: true } : {};
        try {
          while (safety < 50) {
            if (yield* isStale()) return;
            const res = yield call(getAll, {
              route: itemsRoute,
              page,
              limit,
              ...(includeInactive ? {} : { filter: type.activeODataFilter || 'isActive eq true' }),
              ...itemsQuery,
              ...readPriorityParam,
            });
            if (yield* isStale()) return;
            const items = extractItems(res);
            const count = extractTotal(res, total);
            if (items.length === 0) break;

            yield put({
              type: 'homeAppend',
              payload: { type: type.id, items, total: count },
            });

            if (items.length < limit) break;
            page += 1;
            safety += 1;
          }
        } catch (e) {
          if (yield cancelled()) return;
          if (yield* isStale()) return;
          yield put({
            type: 'homeError',
            payload: { type: type.id, error: e?.message || 'error' },
          });
        }
      },
      { type: 'takeLatest' },
    ],

    // Favorite toggle. POST/DELETE against the type's favorites route
    // (every type with `hasFavorites: true` declares one — processes,
    // entities, lists, reports). After the mutation we ONLY rely on
    // the optimistic `homeToggleFavorite` reducer to refresh both the
    // tile and the favorites rail. No follow-up GET is dispatched —
    // re-reading `/manage` here would re-hydrate the favorites rail
    // with stale background-pagination data and reshuffle the list.
    *setFavorite({ payload }, { call, put }) {
      const type = ENTITY_TYPES[payload?.type];
      if (!type || !type.hasFavorites) return;
      const { id, isFavorite } = payload;
      const favRoute = type.routes.favorites;
      if (!favRoute) return;
      try {
        if (isFavorite) {
          yield call(request.delete, `/${favRoute}/${id}`, {});
        } else {
          yield call(request.post, `/${favRoute}/${id}`, {});
        }
        yield put({
          type: 'homeToggleFavorite',
          payload: { type: type.id, id, isFavorite },
        });
      } catch (e) {
        /* swallow — surface via notifications elsewhere if needed */
      }
    },

    *resetHome({ payload }, { put }) {
      yield put({ type: 'homeReset', payload });
    },

    *resetAll(_, { put }) {
      yield put({ type: 'homeResetAll' });
    },

    // Mirrors an item's `isActive` flip into the home slice so the tile
    // re-styles immediately after a kebab toggle. Each entity model
    // already updates its own slice; this keeps the Launchpad snapshot
    // in sync without forcing a refetch.
    *syncItemActive({ payload }, { put }) {
      yield put({ type: 'homeSyncItemActive', payload });
    },
  },

  reducers: {
    homeStart(state, { payload }) {
      const existing = state.home[payload.type] || emptyHome();
      return {
        ...state,
        home: {
          ...state.home,
          [payload.type]: {
            ...existing,
            loading: true,
            loaded: existing.loaded, // keep previous data until save comes in
            error: null,
            epoch: (existing.epoch || 0) + 1,
          },
        },
      };
    },

    homeSave(state, { payload }) {
      // CRITICAL: spread the previous slice first so `epoch` (and any
      // future per-entity fields) survives the save. Without this the
      // `fetchRest` saga that gets dispatched right after `homeSave`
      // sees `epoch=undefined` (treated as 0) vs the captured
      // `myEpoch=N` and immediately aborts as "stale", which used to
      // freeze the launchpad at the first page (e.g. "60 of 66
      // entities" forever). Mirrors `processes/launchpadHomeSave`.
      const prev = state.home[payload.type] || emptyHome();
      return {
        ...state,
        home: {
          ...state.home,
          [payload.type]: {
            ...prev,
            items: payload.items || [],
            groups: payload.groups || [],
            favorites: payload.favorites || [],
            progress: payload.progress || { itemsLoaded: 0, itemsTotal: 0 },
            loading: false,
            loaded: true,
            error: null,
          },
        },
      };
    },

    homeAppend(state, { payload }) {
      const prev = state.home[payload.type] || emptyHome();
      const nextItems = [...prev.items, ...(payload.items || [])];
      return {
        ...state,
        home: {
          ...state.home,
          [payload.type]: {
            ...prev,
            items: nextItems,
            progress: {
              itemsLoaded: nextItems.length,
              itemsTotal: payload.total || nextItems.length,
            },
          },
        },
      };
    },

    homeError(state, { payload }) {
      const prev = state.home[payload.type] || emptyHome();
      return {
        ...state,
        home: {
          ...state.home,
          [payload.type]: { ...prev, loading: false, error: payload.error },
        },
      };
    },

    homeReset(state, { payload }) {
      if (!payload?.type) return state;
      const prev = state.home[payload.type] || emptyHome();
      return {
        ...state,
        home: {
          ...state.home,
          // Conservamos el epoch incrementado: invalida sagas que aún
          // estén en vuelo de la sesión anterior.
          [payload.type]: { ...emptyHome(), epoch: (prev.epoch || 0) + 1 },
        },
      };
    },

    homeResetAll(state) {
      // Bumpea el epoch de cada entidad respetando lo que tenían — así
      // las sagas en vuelo de cualquiera de ellas se invalidan tras un
      // cambio de tenant (`changeCompany`/`changeEnvironment`).
      const home = TYPES.reduce((acc, t) => {
        const prev = state.home[t.id] || emptyHome();
        acc[t.id] = { ...emptyHome(), epoch: (prev.epoch || 0) + 1 };
        return acc;
      }, {});
      return { ...state, home };
    },

    // Updates a single item's `isActive` flag in the home slice for the
    // given entity type. Used to keep the Launchpad in sync after a row
    // toggle dispatched through the legacy module model.
    homeSyncItemActive(state, { payload }) {
      const { type, id, isActive } = payload || {};
      const prev = state.home[type];
      if (!prev || !id) return state;
      const items = prev.items.map((it) => (it.id === id ? { ...it, isActive } : it));
      const favorites = prev.favorites.map((it) => (it.id === id ? { ...it, isActive } : it));
      return {
        ...state,
        home: { ...state.home, [type]: { ...prev, items, favorites } },
      };
    },

    /** Optimistic remove after delete — keeps the grid from flashing empty. */
    homeRemoveItem(state, { payload }) {
      const { type, id } = payload || {};
      const prev = state.home[type];
      if (!prev || !id) return state;
      const key = String(id).toUpperCase();
      const match = (it) => it && String(it.id).toUpperCase() === key;
      const items = prev.items.filter((it) => !match(it));
      const favorites = prev.favorites.filter((it) => !match(it));
      if (items.length === prev.items.length && favorites.length === prev.favorites.length) {
        return state;
      }
      return {
        ...state,
        home: {
          ...state.home,
          [type]: {
            ...prev,
            items,
            favorites,
            progress: {
              itemsLoaded: items.length,
              itemsTotal: Math.max(0, (prev.progress?.itemsTotal || items.length) - 1),
            },
          },
        },
      };
    },

    // Toggle the `isFavorite` flag on the matching record. Types with a
    // dedicated `GET …/favorites` endpoint (processes, entities, lists,
    // reports) ALSO sync the `favorites` array optimistically so the
    // favorites rail reflects the toggle without re-fetching anything —
    // this is what lets the rail stay calm while `/manage` keeps
    // paginating in the background. Allowed-sourced types just flip
    // the flag on `items`; their favorites rail derives from
    // `activeItems.filter(isFavorite)` in `useLaunchpadData`.
    homeToggleFavorite(state, { payload }) {
      const { type, id, isFavorite } = payload;
      const typeConfig = ENTITY_TYPES[type];
      const prev = state.home[type] || emptyHome();
      const next = !isFavorite;
      const items = prev.items.map((it) => (it.id === id ? { ...it, isFavorite: next } : it));
      let favorites = prev.favorites;
      if (typeConfig?.queries?.favorites) {
        if (next) {
          const target = items.find((it) => it.id === id);
          favorites =
            target && !prev.favorites.some((f) => f.id === id)
              ? [...prev.favorites, { ...target, isFavorite: true }]
              : prev.favorites;
        } else {
          favorites = prev.favorites.filter((f) => f.id !== id);
        }
      }
      return {
        ...state,
        home: {
          ...state.home,
          [type]: { ...prev, items, favorites },
        },
      };
    },
  },
};

export default Model;
